import { useMemo } from 'react';

import { useIsFetching } from '@tanstack/react-query';

import { isNotDefined } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { useBuildAsyncValue, useIsValidFieldValues } from 'pages/audience/shared/hooks';
import { useAggregate } from 'pages/audience/shared/hooks/aggregate';
import { selectAudienceAggregateApplied, selectAudienceCartIsSaved } from 'store/selectors';

export const useSavedState = () => {
  const isApplied = useAppSelector(selectAudienceAggregateApplied);
  const isCartSaved = useAppSelector(selectAudienceCartIsSaved);

  const asyncValue = useBuildAsyncValue();
  const { loading: aggregateLoading } = useAggregate();
  const isFetching = useIsFetching();
  const isValid = useIsValidFieldValues();

  const isSaved = useMemo(() => {
    if (isNotDefined(asyncValue)) {
      return false;
    }

    const { type } = asyncValue;

    return type === 'shop' && isCartSaved;
  }, [asyncValue, isCartSaved]);

  const buttonColors = useMemo(() => {
    const hasChanged = !isApplied && isValid && !aggregateLoading;

    return {
      apply: hasChanged ? 'warning' : 'primary',
      save: !isSaved && isValid ? 'warning' : 'primary',
    };
  }, [aggregateLoading, isApplied, isSaved, isValid]);

  const isDisabled = useMemo(
    () => aggregateLoading || isFetching > 0 || !isValid,
    [aggregateLoading, isFetching, isValid]
  );

  return {
    buttonColors,
    isDisabled,
    isSaved,
  };
};
